import { useCallback, useEffect, useRef, useState } from "react"
import ResizeObserver from "resize-observer-polyfill"

// Rounding in the scroll metrics leaves a fraction of a pixel at either end, so
// an edge counts as reached slightly before it is.
const EDGE_EPSILON = 1

type ScrollOverflow = {
  /** Content above the visible area. */
  top: boolean
  /** Content below it. */
  bottom: boolean
}

const NO_OVERFLOW: ScrollOverflow = { top: false, bottom: false }

/**
 * Which vertical edges of a scrolling element have content past them, as
 * `data-overflow-top` / `data-overflow-bottom` attributes a stylesheet can hang
 * an indicator off. Pair with `AUGMENTATION_PANEL_SCROLL_CSS`.
 *
 * Reading the scroll metrics forces layout, so it happens on scroll and when
 * something resizes rather than on render. Between them that's every way the
 * answer can change: the container's box, the content's height, or where the
 * content sits within it.
 */
export default function useScrollOverflow<T extends HTMLElement>() {
  const [node, ref] = useState<T | null>(null)
  const [overflow, setOverflow] = useState<ScrollOverflow>(NO_OVERFLOW)

  // Held in a ref so measuring stays a stable reference — otherwise the
  // subscription below tears down and re-measures on every edge crossing.
  const overflowRef = useRef(overflow)
  overflowRef.current = overflow

  const measure = useCallback(() => {
    if (!node) return

    const { clientHeight, scrollHeight, scrollTop } = node
    const next: ScrollOverflow = {
      top: scrollTop > EDGE_EPSILON,
      bottom: scrollTop + clientHeight < scrollHeight - EDGE_EPSILON
    }

    if (
      next.top !== overflowRef.current.top ||
      next.bottom !== overflowRef.current.bottom
    ) {
      setOverflow(next)
    }
  }, [node])

  useEffect(() => {
    if (!node) return

    measure()
    node.addEventListener("scroll", measure, { passive: true })

    // The container's own box rarely changes — it's the content within it that
    // grows and shrinks — so the children are watched too, and re-watched as a
    // destination swaps them out.
    const sizeObserver = new ResizeObserver(measure)
    const observeContent = () => {
      sizeObserver.disconnect()
      sizeObserver.observe(node)
      Array.from(node.children).forEach((child) => sizeObserver.observe(child))
    }
    observeContent()

    const contentObserver = new MutationObserver(observeContent)
    contentObserver.observe(node, { childList: true })

    return () => {
      node.removeEventListener("scroll", measure)
      sizeObserver.disconnect()
      contentObserver.disconnect()
    }
  }, [node, measure])

  return {
    ref,
    /** Spread onto the scrolling element alongside the ref. */
    props: {
      "data-overflow-top": overflow.top ? "" : undefined,
      "data-overflow-bottom": overflow.bottom ? "" : undefined
    }
  }
}
